/**
 * Team — the people, what they staff, what they cost, and who they carry.
 *
 * The other two tabs answer questions about the BOOK. This one answers questions
 * about the TEAM, and it is where the three things a manager actually
 * administers finally live in one place: who staffs which tier, what each person
 * costs, and which customers each of them ends up with.
 *
 * TWO COSTS, DELIBERATELY. `loadedCostAnnual` on a person is what the team costs
 * TODAY; `loadedCostPerFte` on a tier is what an INCREMENTAL FTE in that tier
 * would cost. Neither substitutes for the other — per-person prices the team you
 * have, per-tier prices the hire you have not made — and the summary below puts
 * them side by side, because the gap between them is the number a staffing case
 * is actually made from.
 *
 * WRITES HERE ARE REAL, unlike the moves on Portfolios. Changing someone's tier
 * writes seats for the current config version, and a cost is payroll. Both say
 * so; neither is a scenario.
 */
import { Fragment, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Kpi,
} from '@andavo/web-ui';
import {
  DetailRow,
  DisclosureCell,
  DisclosureHeaderCell,
  detailRowId,
  TableScroll,
  totalColumns,
  useExpandedRows,
  zebraFor,
} from '@/features/crm/components/ExpandableRow';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { useStaffingData } from '../../StaffingDataProvider';
import {
  addCsm,
  canPublishConfig,
  publishConfig,
  saveSlots,
  setCsmActive,
  setCsmSalary,
  StaffingWriteError,
  toPublishInput,
} from '../../mutations';
import { fetchFormerMembers, type RosterMemberDto } from '../../api';
import {
  buildStaffingModel,
  csmKeyOf,
  rosterKeyIndex,
  StaffingModelError,
} from '../../lib/model';
import {
  parseThreshold,
  pnrs,
  thresholdProblem,
  tierIncentiveRows,
  tierVerdict,
  type TierIncentiveRow,
} from '../../lib/incentive';
import { annualCost, dollars, ftes, hours, volumeM } from '../../lib/format';
import { StaffingScreenState } from '../../components/StaffingScreenState';
import type { Account } from '@andavo/staffing-engine';

const DATA_COLUMNS = (showFinancials: boolean, canManage: boolean) =>
  7 + (showFinancials ? 2 : 0) + (canManage ? 1 : 0);

export default function StaffingTeamPage() {
  const { read, loading, error, reload } = useStaffingData();
  const { can } = useMe();
  const rows = useExpandedRows();
  const [busy, setBusy] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<StaffingWriteError | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  // The incentive plan is edited as a DRAFT and published in one go. Every field
  // in it lives on the config, and the config is immutable-versioned — so a
  // save-per-field editor would write four versions for one sitting and bury the
  // decision that mattered among three intermediate ones. Null/absent means
  // "unchanged"; the stored value shows through.
  /**
   * The last write, and how to put it back.
   *
   * ONE STEP, not a stack, and that is the honest shape rather than a shortcut.
   * Every undo here is a NEW WRITE — restoring a salary is another salary write,
   * reversing a published plan is another published version — so a deep stack
   * would let someone walk backwards through an audit trail that only ever grows
   * forwards, which is a story about the data that is not true. One step covers
   * what people actually need it for: the click they did not mean.
   *
   * It is CLEARED by the next write, because a reversal computed against a
   * superseded state would restore values nobody asked for.
   */
  const [undoable, setUndoable] = useState<{
    label: string;
    /** What reversing it will actually do, when that is not simply "put it back". */
    caveat?: string;
    run: () => Promise<void>;
  } | null>(null);
  const [draftPct, setDraftPct] = useState<string | null>(null);
  const [draftBars, setDraftBars] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState(false);
  // Removal is a two-step confirm because it redistributes a real book. Held as
  // the member's id rather than a boolean so a second click elsewhere cancels
  // the first — a confirm that stays armed while you look at another row is how
  // the wrong person gets removed.
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  // Deactivated people, fetched ON DEMAND. Not part of the shared read set: the
  // provider's `includeInactive` is about which CUSTOMERS the book covers, and
  // widening it would make "show me former colleagues" re-tier the whole book.
  const [former, setFormer] = useState<RosterMemberDto[] | null>(null);
  const [loadingFormer, setLoadingFormer] = useState(false);

  const model = useMemo(() => {
    if (!read) return null;
    try {
      return { ok: true as const, value: buildStaffingModel(read) };
    } catch (err) {
      return {
        ok: false as const,
        message:
          err instanceof StaffingModelError
            ? err.message
            : 'Could not compute the staffing model.',
      };
    }
  }, [read]);

  const state = (
    <StaffingScreenState
      configured={read?.config.configured ?? false}
      error={error}
      loading={loading}
      modelError={model && !model.ok ? model.message : null}
    />
  );
  if (!model || !model.ok || !read) return state;

  const m = model.value;
  const canManage = can('manage:staffing');
  // The SERVER's answer, not the client's belief: a redacted roster read omits
  // the key entirely, and offering an editor over a figure we were not sent
  // would write `undefined` over a real salary.
  const showFinancials = read.roster.meta.financialsRedacted === false;
  const tiers = read.config.config?.tiers ?? [];
  // ONE incentive for the whole team, from the config — one policy set once. A
  // per-person copy would be the same number written N times and N places for
  // it to disagree with itself.
  const incentivePct = read.config.config?.incentivePct ?? 0;

  // Everyone's seats, so a row can show what they staff and a change can be
  // written as a whole list.
  const slotsOf = new Map<string, { tierKey: string; position: number }[]>();
  for (const slot of read.slots.slots) {
    if (slot.csmId === null) continue;
    slotsOf.set(slot.csmId, [
      ...(slotsOf.get(slot.csmId) ?? []),
      { tierKey: slot.tierKey, position: slot.position },
    ]);
  }
  const vacancies = read.slots.slots.filter((s) => s.csmId === null);

  // Engine key → the computed book, so per-person load does not have to be
  // recomputed here from a different set of rules than the other two tabs use.
  const bookOf = new Map<
    string,
    { accounts: Account[]; volumeM: number; hours: number; ftes: number }
  >();
  m.result.portfolios.forEach((portfolio, i) => {
    const load = m.loads[i]!;
    const prev = bookOf.get(portfolio.csm);
    const accounts = [...portfolio.assigned, ...portfolio.pinned];
    bookOf.set(portfolio.csm, {
      accounts: [...(prev?.accounts ?? []), ...accounts],
      volumeM: (prev?.volumeM ?? 0) + load.bookingVolumeM,
      hours: (prev?.hours ?? 0) + load.monthlyHours,
      ftes: (prev?.ftes ?? 0) + load.ftesNeeded,
    });
  });

  const roster = [...read.roster.roster].sort((a, b) =>
    a.displayName.localeCompare(b.displayName),
  );

  // WHAT WE PAY, from the people. Only the recorded ones — a null is unknown,
  // not zero, and summing it as zero would understate payroll by exactly the
  // people nobody has costed yet.
  const costed = roster.filter(
    (r) => typeof r.baseSalaryAnnual === 'number' && r.baseSalaryAnnual > 0,
  );
  const baseAnnual = costed.reduce(
    (sum, r) => sum + (r.baseSalaryAnnual as number),
    0,
  );
  // Derived from base + incentive, never a stored total — see lib/format's
  // annualCost, which mirrors cm-api's own rule.
  const actualAnnual = costed.reduce(
    (sum, r) => sum + (annualCost(r.baseSalaryAnnual, incentivePct) ?? 0),
    0,
  );
  const uncosted = roster.length - costed.length;

  // WHAT THE MODEL SAYS IT SHOULD COST, from the tiers — the incremental-FTE
  // price of the work the book actually implies.
  const modelAnnual = m.tierLoads.reduce((sum, t) => {
    const tier = tiers.find((x) => x.key === t.tier);
    return sum + (tier?.loadedCostPerFte ?? 0) * t.ftesNeeded;
  }, 0);

  const tierLabel = (key: string) =>
    tiers.find((t) => t.key === key)?.label ?? key;

  /**
   * The seat list exactly as it stands, in the shape `saveSlots` takes.
   *
   * Captured BEFORE a write so an undo can restore it wholesale. Restoring the
   * whole list rather than reversing one move is what makes the undo exact: the
   * route replaces the list anyway, and position is load-bearing, so putting
   * back the literal previous list cannot leave the tier ordered differently
   * from how it started.
   */
  const seatSnapshot = () =>
    read.slots.slots.map((s) => ({
      tierKey: s.tierKey,
      position: s.position,
      csmId: s.csmId,
    }));

  /** Move one person to a tier, by rewriting the whole seat list. */
  const moveToTier = async (csmId: string, toTierKey: string) => {
    setBusy(csmId);
    setWriteError(null);
    setSaved(null);
    setUndoable(null);
    const before = seatSnapshot();
    const fromTier = slotsOf.get(csmId)?.[0]?.tierKey;
    const who =
      roster.find((r) => r.id === csmId)?.displayName ?? 'that person';
    try {
      const keep = read.slots.slots.filter((s) => s.csmId !== csmId);
      const next = [
        ...keep.map((s) => ({
          tierKey: s.tierKey,
          position: s.position,
          csmId: s.csmId,
        })),
        // At the END of the destination — position is load-bearing, and putting
        // someone at the front would silently redistribute the existing team's
        // books as a side effect of a change about one person.
        ...(toTierKey === ''
          ? []
          : [
              {
                tierKey: toTierKey,
                position: keep.filter((s) => s.tierKey === toTierKey).length,
                csmId,
              },
            ]),
      ];
      await saveSlots(renumber(next));
      setSaved(csmId);
      setUndoable({
        label:
          toTierKey === ''
            ? `${who} was taken off every tier`
            : `${who} was moved to ${tierLabel(toTierKey)}${fromTier ? ` from ${tierLabel(fromTier)}` : ''}`,
        run: () => saveSlots(before).then(() => undefined),
      });
      reload();
    } catch (err) {
      setWriteError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not save the team.', 0),
      );
    } finally {
      setBusy(null);
    }
  };

  const saveSalary = async (member: (typeof roster)[number], raw: string) => {
    const trimmed = raw.trim();
    // Empty means "unknown", which is a real and different state from zero.
    const value = trimmed === '' ? null : Number(trimmed);
    if (value !== null && (!Number.isFinite(value) || value < 0)) return;
    // Nothing changed, so there is nothing to save and nothing to undo. Blurring
    // a field you only looked at must not put a no-op in the audit trail — nor
    // arm an Undo that would restore the value it already has.
    if ((member.baseSalaryAnnual ?? null) === value) return;
    const before = member.baseSalaryAnnual ?? null;
    setBusy(member.id);
    setWriteError(null);
    setSaved(null);
    setUndoable(null);
    try {
      await setCsmSalary(member, value);
      setSaved(member.id);
      setUndoable({
        label:
          value === null
            ? `${member.displayName}'s salary was cleared`
            : `${member.displayName}'s salary was set to ${dollars(value)}`,
        run: () => setCsmSalary(member, before),
      });
      reload();
    } catch (err) {
      setWriteError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not save that salary.', 0),
      );
    } finally {
      setBusy(null);
    }
  };

  // ── the roster ────────────────────────────────────────────────────────────

  /**
   * Remove someone from the team.
   *
   * TWO WRITES, IN THIS ORDER, and the order is the whole safety argument. A
   * seat pointing at a deactivated person is the one genuinely bad state: the
   * engine distributes work to whoever holds a seat and never consults `active`,
   * so a departed CSM would keep receiving customers while being invisible on
   * every screen. Clearing the seats first fails safe — the worst outcome of a
   * half-completed removal is somebody active holding no seats, which is
   * visible here rather than silent in the model.
   *
   * NOT A DELETE. cm-api has no delete for roster rows on purpose: pins and
   * snapshots reference them, so history would either block the write or go with
   * it. This deactivates, which is also what makes it reversible.
   *
   * PINS ARE LEFT ALONE. A pin is a deliberate decision about a customer, not a
   * side effect of who happens to be employed, and the diagnostics already treat
   * a pin to an inactive person as exactly the stale rule a review should see.
   * Silently dropping them here would discard decisions nobody revisited.
   */
  const removeMember = async (member: (typeof roster)[number]) => {
    setBusy(member.id);
    setWriteError(null);
    setSaved(null);
    setUndoable(null);
    const before = seatSnapshot();
    try {
      const seats = slotsOf.get(member.id) ?? [];
      if (seats.length > 0) {
        await saveSlots(
          renumber(
            read.slots.slots
              .filter((s) => s.csmId !== member.id)
              .map((s) => ({
                tierKey: s.tierKey,
                position: s.position,
                csmId: s.csmId,
              })),
          ),
        );
      }
      await setCsmActive(member, false);
      setConfirmRemove(null);
      setSaved(member.id);
      // Anyone listing former members is now looking at a stale list.
      setFormer(null);
      setUndoable({
        label: `${member.displayName} was removed from the team`,
        run: async () => {
          // Reactivate FIRST, then restore the seats. The mirror of the removal
          // order, and for the same reason: a seat may only point at somebody
          // the roster can resolve, so putting the seats back before the person
          // exists again would recreate the exact silent state removal avoids.
          await setCsmActive(member, true);
          await saveSlots(before);
        },
      });
      reload();
    } catch (err) {
      setWriteError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not remove that person.', 0),
      );
    } finally {
      setBusy(null);
    }
  };

  /** Put someone back on the roster. They return holding NO seats. */
  const restoreMember = async (member: RosterMemberDto) => {
    setBusy(member.id);
    setWriteError(null);
    setSaved(null);
    try {
      await setCsmActive(member, true);
      setFormer(null);
      setSaved(member.id);
      setUndoable({
        label: `${member.displayName} was restored to the team`,
        caveat:
          'They come back holding no seats, so undoing this only deactivates them again — it does not put seats back that restoring did not give them.',
        run: () => setCsmActive(member, false),
      });
      reload();
    } catch (err) {
      setWriteError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not restore that person.', 0),
      );
    } finally {
      setBusy(null);
    }
  };

  /**
   * Put the last change back.
   *
   * ITSELF A WRITE, and it clears the undo afterwards rather than arming a redo:
   * every reversal here appends to the same history it is reversing, so an
   * undo/redo pair would let someone oscillate while the audit trail grew a row
   * each time. One step back, then you are looking at the current state again.
   */
  const runUndo = async () => {
    if (!undoable) return;
    setBusy('undo');
    setWriteError(null);
    setSaved(null);
    try {
      await undoable.run();
      setUndoable(null);
      setFormer(null);
      discardPlan();
      reload();
    } catch (err) {
      setWriteError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not undo that.', 0),
      );
    } finally {
      setBusy(null);
    }
  };

  const showFormer = async () => {
    setLoadingFormer(true);
    setWriteError(null);
    try {
      setFormer(await fetchFormerMembers());
    } catch (err) {
      setWriteError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError(
              'Could not load the people who have been removed.',
              0,
            ),
      );
    } finally {
      setLoadingFormer(false);
    }
  };

  // ── the incentive plan ────────────────────────────────────────────────────
  //
  // Two things a manager sets, held together because they are one policy: what
  // the incentive PAYS (a percentage of base, one rate for everyone) and what
  // each tier has to BOOK to earn it.

  /** The bar as it currently reads in the editor — draft first, stored behind. */
  const barText = (tier: (typeof tiers)[number]) =>
    draftBars[tier.key] ??
    (tier.incentivePnrThreshold == null
      ? ''
      : String(tier.incentivePnrThreshold));
  const barTextOf = (tierKey: string) => {
    const tier = tiers.find((t) => t.key === tierKey);
    return tier ? barText(tier) : (draftBars[tierKey] ?? '');
  };
  const pctText = draftPct ?? String(incentivePct);

  /** Tiers with the DRAFT bars applied, so typing one re-reads who clears it. */
  const draftTiers = tiers.map((t) => ({
    ...t,
    incentivePnrThreshold: parseThreshold(barText(t)),
  }));

  // Every problem across the whole draft, not the first — a config version is
  // immutable and the server collects its problems the same way, so a caller
  // fixing one should get the list rather than a round trip per typo.
  const planProblems: string[] = [];
  const pctValue = Number(pctText.trim());
  if (!Number.isFinite(pctValue) || pctValue < 0 || pctValue > 100)
    planProblems.push(
      'The payout must be a number between 0 and 100 — it is a percentage of base salary.',
    );
  for (const tier of tiers) {
    const problem = thresholdProblem(barText(tier));
    if (problem)
      planProblems.push(`${tier.label}: the monthly PNR bar ${problem}.`);
  }

  // Has anyone TOUCHED the plan — independent of whether what they typed is
  // valid. Kept separate from the change list below because the two answer
  // different questions: this one decides whether to show the editor's controls
  // at all, and a half-typed "1500." must not make them vanish mid-edit.
  const planTouched =
    pctText !== String(incentivePct) ||
    tiers.some(
      (tier) =>
        barText(tier) !==
        (tier.incentivePnrThreshold == null
          ? ''
          : String(tier.incentivePnrThreshold)),
    );

  // What would actually change, in words — and the real dirty check. Typing a
  // value and typing it back leaves nothing to publish, because a button
  // offering to publish an identical version would add a row to the audit trail
  // recording no decision. Computed only from a VALID draft: describing a change
  // to "1,501 PNRs" when the field holds 1500.5 would put a number on screen
  // that nothing would ever store.
  const planChanges: string[] = [];
  if (planProblems.length === 0) {
    if (pctValue !== incentivePct)
      planChanges.push(`payout ${incentivePct}% → ${pctValue}% of base salary`);
    for (const tier of tiers) {
      const next = parseThreshold(barText(tier));
      const prev = tier.incentivePnrThreshold ?? null;
      if (next === prev) continue;
      planChanges.push(
        `${tier.label} ${prev === null ? 'no bar' : `${pnrs(prev)} PNRs`} → ${
          next === null ? 'no bar' : `${pnrs(next)} PNRs`
        }`,
      );
    }
  }

  const discardPlan = () => {
    setDraftPct(null);
    setDraftBars({});
  };

  /**
   * Publish the plan — ONE config version carrying every change in the draft.
   *
   * Heavier than a salary edit and it says so: versions are immutable, so this
   * is the row someone reads later to answer "when did the Enterprise bar move,
   * and who moved it".
   */
  const publishPlan = async () => {
    if (
      !read.config.config ||
      planProblems.length > 0 ||
      planChanges.length === 0
    )
      return;
    setBusy('plan');
    setWriteError(null);
    setSaved(null);
    setUndoable(null);
    const previous = read.config.config;
    try {
      await publishConfig(
        toPublishInput(read.config.config, {
          tiers: draftTiers,
          incentivePct: pctValue,
        }),
      );
      // Cleared BEFORE the reload, not after: the draft's whole purpose is to
      // differ from what is stored, and leaving it set would make the freshly
      // published values read as unsaved changes against themselves.
      discardPlan();
      setSaved('plan');
      setUndoable({
        label: `the incentive plan was published as a new version`,
        // Said out loud because it is the one undo here that does NOT restore
        // the previous state of the world. Config versions are immutable and
        // append-only; the old values come back by publishing them AGAIN, so the
        // history keeps both the change and its reversal. That is the correct
        // behaviour for an audit trail and the wrong thing to let someone
        // discover afterwards.
        caveat:
          'Versions are immutable, so this publishes a further version restoring the previous figures rather than deleting the one just written.',
        run: () =>
          publishConfig(toPublishInput(previous!, {})).then(() => undefined),
      });
      reload();
    } catch (err) {
      setWriteError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not publish the incentive plan.', 0),
      );
    } finally {
      setBusy(null);
    }
  };

  // Who is measured against each bar. Built from the DRAFT tiers so that typing
  // a number answers "could anyone hit this?" before it is published — the
  // question the bar exists to settle.
  const incentiveRows = tierIncentiveRows({
    tiers: draftTiers,
    slots: read.slots.slots,
    keyById: rosterKeyIndex(read.roster.roster),
    bookOf,
    byTier: m.result.byTier,
    pnrs90: read.accounts.pnrs90,
    displayName: m.displayName,
  });
  const pnrsUnrecorded = read.accounts.meta.pnrsUnrecorded ?? 0;

  const publishable = canPublishConfig(read.config.config, canManage);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="People on the roster" value={String(roster.length)} />
        <Kpi label="Planned hires" value={String(vacancies.length)} />
        <Kpi
          label="Cost at target"
          value={showFinancials ? dollars(actualAnnual) : '—'}
        />
        <Kpi
          label="Model says"
          value={showFinancials ? dollars(modelAnnual) : '—'}
        />
      </div>

      {showFinancials ? (
        <Card>
          <CardHeader>
            <CardTitle>
              What the team costs at target, and what the model says
            </CardTitle>
          </CardHeader>
          <CardContent
            className="flex flex-col gap-2 text-sm"
            data-testid="staffing-team-costs"
          >
            {/* The two costs, side by side, because the GAP is the number a
                staffing case is made from — not either figure alone.

                The incentive RATE used to be editable right here. It moved to
                the Incentive section below, where it sits next to the thing it
                is earned against — a payout percentage and the transaction bars
                that trigger it are one plan, and splitting them across two cards
                let each be changed without the other in view. */}
            <p className="text-content-secondary">
              {/* "At target" is load-bearing, not a nicety. The incentive is
                  earned against performance, so this is what the team costs
                  when everyone hits target — a reader who takes it for actual
                  payroll is wrong by however far the team lands from target,
                  in either direction. */}
              Base salaries total {dollars(baseAnnual)}; cost at target adds the{' '}
              {incentivePct}% OTE incentive on top. It covers the{' '}
              {costed.length} {costed.length === 1 ? 'person' : 'people'} with a
              salary recorded
              {uncosted > 0 ? (
                <>
                  {' '}
                  — <strong>{uncosted}</strong>{' '}
                  {uncosted === 1 ? 'has' : 'have'} none, and{' '}
                  {uncosted === 1 ? 'is' : 'are'} not in that total
                </>
              ) : null}
              . Actual cost moves with performance. “Model says” prices the work
              the book implies, at each tier’s cost per FTE.
            </p>
            <p className="text-content-secondary">
              {modelAnnual > actualAnnual ? (
                <>
                  The model is{' '}
                  <strong>{dollars(modelAnnual - actualAnnual)}</strong> above
                  cost at target — the book implies more service time than the
                  team costs when everyone hits target.
                </>
              ) : (
                <>
                  Cost at target is{' '}
                  <strong>{dollars(actualAnnual - modelAnnual)}</strong> above
                  the model — the team costs more than the book implies at these
                  tier rates.
                </>
              )}{' '}
              {uncosted > 0
                ? 'Read that gap carefully while anyone is uncosted.'
                : null}
            </p>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Incentive</CardTitle>
        </CardHeader>
        <CardContent
          className="flex flex-col gap-4 text-sm"
          data-testid="staffing-incentive"
        >
          <p className="text-content-secondary">
            One plan in one place: what the incentive pays, and what each tier
            has to book to earn it. Both live on the configuration, so a change
            here publishes a new version and the plan is dated and attributed.
          </p>

          {/* THE PAYOUT. One rate for the whole team — a per-person copy would
              be the same number written N times and N places for it to disagree
              with itself. */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Only a real control gets `htmlFor` — when the payout is
                redacted the value is a span, and a label pointing at a
                non-labellable element is invalid and unreachable to a screen
                reader. */}
            {showFinancials ? (
              <label className="font-medium" htmlFor="staffing-incentive-pct">
                Pays at target (OTE)
              </label>
            ) : (
              <span className="font-medium">Pays at target (OTE)</span>
            )}
            {showFinancials ? (
              <>
                <input
                  className="w-24 rounded border border-border-medium bg-surface-background px-2 py-1 text-right text-sm"
                  disabled={!canManage || !publishable.allowed || busy !== null}
                  id="staffing-incentive-pct"
                  max={100}
                  min={0}
                  onChange={(e) => setDraftPct(e.target.value)}
                  step="0.5"
                  type="number"
                  value={pctText}
                />
                <span className="text-content-secondary">
                  % of base salary, the same rate for everyone — earned in
                  monthly instalments, each gated by that month’s bar below.
                </span>
              </>
            ) : (
              /* The payout is a component of pay, so a redacted read does not
                 carry it — and rendering the 0 that an absent key would coerce
                 to would say "there is no incentive" instead of "you may not
                 see it". The BARS below are not redacted: they are booking
                 targets, and disclose nothing about anyone's salary. */
              <span className="text-content-secondary">
                — not shown in this view, which has the cost model redacted.
              </span>
            )}
          </div>

          {/* THE BARS. */}
          <TableScroll testId="staffing-incentive-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th className="text-right">Monthly PNRs to bonus</th>
                  <th>Measured against, in an average month</th>
                  <th className="text-right">Tier’s own book</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {incentiveRows.map((row, index) => (
                  <IncentiveTierRow
                    canEdit={canManage && publishable.allowed && busy === null}
                    key={row.tierKey}
                    onChange={(value) =>
                      setDraftBars((prev) => ({
                        ...prev,
                        [row.tierKey]: value,
                      }))
                    }
                    row={row}
                    text={barTextOf(row.tierKey)}
                    tierLabel={tierLabel}
                    zebra={zebraFor(index)}
                  />
                ))}
              </tbody>
            </table>
          </TableScroll>

          <p className="text-content-secondary text-xs">
            {/* The measurement caveat, stated where the numbers are rather than
                in a tooltip. The bar is MONTHLY because the incentive is
                calculated and paid monthly; the CRM records a trailing-90-day
                PNR count, so the measured column is the average month inside
                that window — an average OF the window, not a forecast beyond it,
                and still not the month anyone is actually paid on. */}
            Bars are <strong>monthly PNRs per CSM</strong> — the incentive is
            calculated and paid monthly, so each month is gated on its own. The
            tier sets the bar; everyone staffing it is measured against that
            same bar, on their <strong>whole book</strong> rather than the slice
            of it that tiers here, because that is what a bonus is earned on.
            Somebody holding seats in two tiers is measured once, under the tier
            they are assigned to in the table below — this tab is where that is
            decided. Each figure is the <strong>average month</strong> across
            their trailing 90 days, so a strong month and a weak one inside that
            window average to a figure neither of them was. The tier’s own book
            is what these customers generate whoever holds them, so it can
            exceed the people staffing the tier — a customer can be pinned
            elsewhere.
            {pnrsUnrecorded > 0 ? (
              <>
                {' '}
                <strong>{pnrsUnrecorded}</strong>{' '}
                {pnrsUnrecorded === 1 ? 'customer has' : 'customers have'} no
                PNR data recorded and {pnrsUnrecorded === 1 ? 'is' : 'are'} left
                out of every figure here rather than counted as zero.
              </>
            ) : null}
          </p>

          {planProblems.length > 0 ? (
            <div className="flex flex-col gap-1" role="alert">
              {planProblems.map((problem) => (
                <p className="text-content-danger text-xs" key={problem}>
                  {problem}
                </p>
              ))}
            </div>
          ) : null}

          {canManage && !publishable.allowed ? (
            <p className="text-content-secondary text-xs">
              {publishable.reason}
            </p>
          ) : null}

          {/* One publish for the whole plan. Editing four fields and writing
              four immutable versions would bury the decision that mattered among
              three intermediate ones. */}
          {planTouched ? (
            <div className="flex flex-col gap-2">
              {planChanges.length > 0 ? (
                <p className="text-content-warning text-xs">
                  Not saved yet —{' '}
                  {planChanges.length === 1
                    ? '1 change'
                    : `${planChanges.length} changes`}
                  : {planChanges.join('; ')}.
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  disabled={
                    busy !== null ||
                    planProblems.length > 0 ||
                    // Touched but identical — "20." parses to the stored 20.
                    // The button stays visible so the Discard beside it does,
                    // but there is no version to write.
                    planChanges.length === 0
                  }
                  onClick={publishPlan}
                  size="sm"
                >
                  Publish incentive plan
                </Button>
                <Button
                  disabled={busy !== null}
                  onClick={discardPlan}
                  size="sm"
                  variant="secondary"
                >
                  Discard
                </Button>
              </div>
            </div>
          ) : saved === 'plan' ? (
            <p className="text-content-success text-xs" role="status">
              Incentive plan published as a new configuration version.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {writeError ? (
        <div className="flex flex-col gap-1" role="alert">
          <p className="text-content-danger text-sm">{writeError.message}</p>
          {writeError.problems.map((p) => (
            <p className="text-content-danger text-xs" key={p}>
              {p}
            </p>
          ))}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Team</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {canManage ? (
            <p className="text-content-warning text-sm">
              Changes on this tab are SAVED, not previewed — a tier change
              writes the team’s seats and a salary is payroll. Use Portfolios to
              try a move without committing to it.
            </p>
          ) : null}

          {/* THE LAST CHANGE, AND THE WAY BACK. One step, because every
              reversal here is itself a write appending to the history it
              reverses — a deep stack would tell a story about the data that is
              not true. It covers what it needs to: the click you did not mean. */}
          {canManage && undoable ? (
            <div
              className="flex flex-wrap items-center gap-2 rounded-md border border-border-medium bg-surface-background px-3 py-2"
              data-testid="staffing-team-undo"
              role="status"
            >
              <span className="text-sm">Saved: {undoable.label}.</span>
              <Button
                disabled={busy !== null}
                onClick={runUndo}
                size="sm"
                variant="secondary"
              >
                {busy === 'undo' ? 'Undoing…' : 'Undo'}
              </Button>
              {undoable.caveat ? (
                <span className="text-content-secondary text-xs">
                  {undoable.caveat}
                </span>
              ) : null}
            </div>
          ) : null}

          {canManage ? (
            adding ? (
              <AddMemberForm
                busy={busy !== null}
                onAdded={(added) => {
                  setAdding(false);
                  setSaved('added');
                  setUndoable({
                    label: `${added.displayName} was added to the roster`,
                    caveat:
                      'Roster rows are never deleted, so undoing this deactivates them — they will appear under "people who were removed".',
                    run: () => setCsmActive(added, false),
                  });
                  reload();
                }}
                onCancel={() => setAdding(false)}
                onError={setWriteError}
                setBusy={setBusy}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  disabled={busy !== null}
                  onClick={() => setAdding(true)}
                  size="sm"
                  variant="secondary"
                >
                  Add someone
                </Button>
                {saved === 'added' ? (
                  <span className="text-content-success text-xs" role="status">
                    added to the roster
                  </span>
                ) : null}
              </div>
            )
          ) : null}

          <TableScroll testId="staffing-team-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <DisclosureHeaderCell />
                  <th>CSM</th>
                  <th>Role</th>
                  <th>Staffs</th>
                  <th className="text-right">Customers</th>
                  <th className="text-right">Booking volume</th>
                  <th className="text-right">Hours / mo</th>
                  <th className="text-right">FTEs</th>
                  {showFinancials ? (
                    <>
                      <th className="text-right">Base salary</th>
                      <th className="text-right">Annual cost</th>
                    </>
                  ) : null}
                  {canManage ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {roster.map((member, index) => {
                  const key = csmKeyOf(member);
                  const book = bookOf.get(key);
                  const seats = slotsOf.get(member.id) ?? [];
                  const id = detailRowId('staffing-team', member.id);
                  const open = rows.isExpanded(member.id);
                  const zebra = zebraFor(index);
                  return (
                    <Fragment key={member.id}>
                      <tr data-zebra={zebra}>
                        <DisclosureCell
                          controls={id}
                          label={member.displayName}
                          onToggle={() => rows.toggle(member.id)}
                          open={open}
                        />
                        <td className="font-medium">
                          {member.displayName}
                          {saved === member.id ? (
                            <span
                              className="ml-2 text-content-success text-xs"
                              role="status"
                            >
                              saved
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <Badge variant="default">{member.role}</Badge>
                        </td>
                        <td>
                          {canManage ? (
                            <select
                              aria-label={`Tier staffed by ${member.displayName}`}
                              className="rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
                              disabled={busy === member.id}
                              onChange={(e) =>
                                moveToTier(member.id, e.target.value)
                              }
                              value={seats[0]?.tierKey ?? ''}
                            >
                              {/* A manager legitimately staffs no tier while
                                  holding pinned accounts — the engine models
                                  exactly that, so it is an option and not a gap. */}
                              <option value="">No tier</option>
                              {tiers.map((t) => (
                                <option key={t.key} value={t.key}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          ) : seats.length === 0 ? (
                            <span className="text-content-secondary">
                              No tier
                            </span>
                          ) : (
                            seats.map((s) => tierLabel(s.tierKey)).join(', ')
                          )}
                          {seats.length > 1 ? (
                            <span
                              className="ml-2 text-content-secondary text-xs"
                              title="More than one seat in a tier is how 'most of their time is here' gets expressed."
                            >
                              ×{seats.length}
                            </span>
                          ) : null}
                        </td>
                        <td className="text-right tabular-nums">
                          {book?.accounts.length ?? 0}
                        </td>
                        <td className="text-right tabular-nums">
                          {volumeM(book?.volumeM ?? 0)}
                        </td>
                        <td className="text-right tabular-nums">
                          {hours(book?.hours ?? 0)}
                        </td>
                        <td className="text-right tabular-nums">
                          {ftes(book?.ftes ?? 0)}
                        </td>
                        {showFinancials ? (
                          <>
                            <td className="text-right tabular-nums">
                              {canManage ? (
                                <input
                                  aria-label={`Base salary for ${member.displayName}`}
                                  className="w-28 rounded border border-border-medium bg-surface-background px-2 py-1 text-right text-sm"
                                  defaultValue={member.baseSalaryAnnual ?? ''}
                                  disabled={busy === member.id}
                                  min={0}
                                  // Committed on BLUR, not per keystroke: this
                                  // is a write, and saving "1", "17", "172" on
                                  // the way to 172000 would put three wrong
                                  // salaries in the audit trail.
                                  onBlur={(e) =>
                                    saveSalary(member, e.target.value)
                                  }
                                  placeholder="not recorded"
                                  step="1000"
                                  type="number"
                                />
                              ) : member.baseSalaryAnnual == null ? (
                                <span
                                  className="text-content-secondary"
                                  title="No salary has been recorded for this person."
                                >
                                  —
                                </span>
                              ) : (
                                dollars(member.baseSalaryAnnual)
                              )}
                            </td>
                            <td className="text-right tabular-nums">
                              {/* DERIVED, and read-only for exactly that
                                  reason: an editable total could be set to
                                  something base × incentive does not produce,
                                  and then two numbers would claim to be the
                                  same fact. */}
                              {annualCost(
                                member.baseSalaryAnnual,
                                incentivePct,
                              ) === null ? (
                                <span
                                  className="text-content-secondary"
                                  title="No salary recorded, so there is no cost to derive. Not zero."
                                >
                                  —
                                </span>
                              ) : (
                                dollars(
                                  annualCost(
                                    member.baseSalaryAnnual,
                                    incentivePct,
                                  )!,
                                )
                              )}
                            </td>
                          </>
                        ) : null}
                        {canManage ? (
                          <td className="text-right">
                            <Button
                              disabled={busy !== null}
                              onClick={() =>
                                setConfirmRemove(
                                  confirmRemove === member.id
                                    ? null
                                    : member.id,
                                )
                              }
                              size="link"
                              variant="link"
                            >
                              {confirmRemove === member.id
                                ? 'Cancel'
                                : 'Remove'}
                            </Button>
                          </td>
                        ) : null}
                      </tr>
                      {confirmRemove === member.id ? (
                        <DetailRow
                          colSpan={totalColumns(
                            DATA_COLUMNS(showFinancials, canManage),
                          )}
                          id={`staffing-team-remove-${member.id}`}
                          testId={`staffing-team-remove-${member.id}`}
                          zebra={zebra}
                        >
                          <RemoveMemberConfirm
                            busy={busy !== null}
                            customers={book?.accounts.length ?? 0}
                            name={member.displayName}
                            onCancel={() => setConfirmRemove(null)}
                            onConfirm={() => removeMember(member)}
                            pins={
                              read.pins.pins.filter(
                                (pin) => pin.csmId === member.id,
                              ).length
                            }
                            seats={seats.map((seat) => tierLabel(seat.tierKey))}
                          />
                        </DetailRow>
                      ) : null}
                      {open ? (
                        <DetailRow
                          colSpan={totalColumns(
                            DATA_COLUMNS(showFinancials, canManage),
                          )}
                          id={id}
                          testId={`staffing-team-book-${member.id}`}
                          zebra={zebra}
                        >
                          <TeamBook
                            accounts={book?.accounts ?? []}
                            name={member.displayName}
                          />
                        </DetailRow>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>

          {vacancies.length > 0 ? (
            <p className="text-content-secondary text-sm">
              {vacancies.length} planned{' '}
              {vacancies.length === 1 ? 'hire' : 'hires'} —{' '}
              {vacancies.map((v) => tierLabel(v.tierKey)).join(', ')}. No work
              is distributed to a vacancy, so the FTE figures above exclude
              {vacancies.length === 1 ? ' it' : ' them'}.
            </p>
          ) : null}

          {/* REMOVED PEOPLE ARE STILL THERE. Nothing here deletes a roster row —
              pins and snapshots reference them and history has to stay readable
              — so "removed" means deactivated, and this is what makes that
              reversible from the screen rather than only from the database. */}
          {canManage ? (
            former === null ? (
              <div>
                <Button
                  disabled={loadingFormer || busy !== null}
                  onClick={showFormer}
                  size="link"
                  variant="link"
                >
                  {loadingFormer ? 'Loading…' : 'Show people who were removed'}
                </Button>
              </div>
            ) : (
              <div
                className="flex flex-col gap-2"
                data-testid="staffing-former"
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <h4 className="font-medium text-sm">Removed from the team</h4>
                  <Button
                    onClick={() => setFormer(null)}
                    size="link"
                    variant="link"
                  >
                    hide
                  </Button>
                </div>
                {former.length === 0 ? (
                  <p className="text-content-secondary text-sm">
                    Nobody has been removed.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {former.map((member) => (
                      <li
                        className="flex flex-wrap items-center gap-2 text-sm"
                        key={member.id}
                      >
                        <span>{member.displayName}</span>
                        <Badge variant="default">{member.role}</Badge>
                        <Button
                          disabled={busy !== null}
                          onClick={() => restoreMember(member)}
                          size="link"
                          variant="link"
                        >
                          Restore
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-content-secondary text-xs">
                  Their history stays readable — past scenarios and snapshots
                  that named them still resolve. Restoring puts someone back on
                  the roster holding no seats; give them a tier afterwards.
                </p>
              </div>
            )
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

/** Close the gaps in each tier's seat positions, preserving order. */
function renumber(
  slots: readonly { tierKey: string; position: number; csmId: string | null }[],
): { tierKey: string; position: number; csmId: string | null }[] {
  const byTier = new Map<string, number>();
  return [...slots]
    .sort(
      (a, b) => a.tierKey.localeCompare(b.tierKey) || a.position - b.position,
    )
    .map((s) => {
      const next = byTier.get(s.tierKey) ?? 0;
      byTier.set(s.tierKey, next + 1);
      return { ...s, position: next };
    });
}

/**
 * Add someone to the roster.
 *
 * IDENTITY IS THE POINT OF THIS FORM, not the name. The roster joins to
 * `customer_settings.csm_external_id` / `csm_email`, and a display name is never
 * a join key — "Taylor R. Daily" and "Taylor Daily" compare unequal and empty a
 * book, which is the 2026-08-10 failure this module refuses to repeat. So one of
 * the two identifiers is required, and the form says why rather than showing a
 * red box after a round trip.
 *
 * NO SALARY FIELD. Adding someone and costing them are different decisions with
 * different authorities: the salary column needs `view:staffing_financials`, and
 * putting it here would either block a manager from adding a colleague or ask
 * them to fill in a number they cannot see. The row lands uncosted, which is
 * true, and the table says so.
 */
function AddMemberForm({
  busy,
  onAdded,
  onCancel,
  onError,
  setBusy,
}: {
  busy: boolean;
  onAdded: (added: {
    id: string;
    displayName: string;
    externalId: string | null;
    email: string | null;
    role: string;
  }) => void;
  onCancel: () => void;
  onError: (err: StaffingWriteError) => void;
  setBusy: (next: string | null) => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [externalId, setExternalId] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'csm' | 'manager'>('csm');

  const problems: string[] = [];
  if (displayName.trim() === '') problems.push('A name is required.');
  if (externalId.trim() === '' && email.trim() === '')
    problems.push(
      'An external id or an email is required — the roster joins to the CRM on those, and a row with neither could never own an account.',
    );

  const submit = async () => {
    if (problems.length > 0) return;
    setBusy('add');
    try {
      const member = {
        displayName: displayName.trim(),
        role,
        externalId: externalId.trim() || null,
        email: email.trim() || null,
      };
      const { id } = await addCsm(member);
      onAdded({ ...member, id });
    } catch (err) {
      onError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not add that person.', 0),
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="flex flex-col gap-2 rounded border border-border-medium p-3"
      data-testid="staffing-add-member"
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          Name
          <input
            aria-label="Name"
            className="w-48 rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
            onChange={(e) => setDisplayName(e.target.value)}
            type="text"
            value={displayName}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Role
          <select
            aria-label="Role"
            className="rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
            onChange={(e) => setRole(e.target.value as 'csm' | 'manager')}
            value={role}
          >
            <option value="csm">csm</option>
            <option value="manager">manager</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          External id
          <input
            aria-label="External id"
            className="w-40 rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
            onChange={(e) => setExternalId(e.target.value)}
            type="text"
            value={externalId}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Email
          <input
            aria-label="Email"
            className="w-56 rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            value={email}
          />
        </label>
      </div>
      <p className="text-content-secondary text-xs">
        The external id or email is how this person is matched to the customers
        they own in the CRM. A name alone cannot do it.
      </p>
      {problems.map((problem) => (
        <p className="text-content-secondary text-xs" key={problem}>
          {problem}
        </p>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          disabled={busy || problems.length > 0}
          onClick={submit}
          size="sm"
        >
          Add to roster
        </Button>
        <Button onClick={onCancel} size="sm" variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * What removing someone will actually do, before it happens.
 *
 * Spelled out rather than a yes/no, because the consequences reach past the row
 * being clicked: their seats are cleared, and every customer they hold is
 * redistributed to whoever else staffs those tiers. That is a change to other
 * people's books made by a button labelled with one person's name, and it should
 * not be a surprise.
 */
function RemoveMemberConfirm({
  busy,
  customers,
  name,
  onCancel,
  onConfirm,
  pins,
  seats,
}: {
  busy: boolean;
  customers: number;
  name: string;
  onCancel: () => void;
  onConfirm: () => void;
  pins: number;
  seats: readonly string[];
}) {
  return (
    <div className="detail-panel flex flex-col gap-2">
      <p className="text-sm">
        Remove <strong>{name}</strong> from the team?
      </p>
      <ul className="flex list-disc flex-col gap-1 pl-5 text-content-secondary text-sm">
        <li>
          {seats.length === 0
            ? 'They staff no tier, so no seats change.'
            : `Their ${seats.length === 1 ? 'seat' : `${seats.length} seats`} in ${seats.join(', ')} ${seats.length === 1 ? 'is' : 'are'} cleared.`}
        </li>
        <li>
          {customers === 0
            ? 'They hold no customers in the computed book.'
            : customers === 1
              ? "Their 1 customer is redistributed to whoever else staffs that tier — somebody else's book changes."
              : `Their ${customers} customers are redistributed to whoever else staffs those tiers — other people's books change.`}
        </li>
        {pins > 0 ? (
          <li>
            {pins} {pins === 1 ? 'customer stays' : 'customers stay'} pinned to
            them. Pins are decisions somebody made, so they are left for review
            rather than dropped — they will show as pinned to an inactive
            person.
          </li>
        ) : null}
        <li>
          Nothing is deleted. They are deactivated, past scenarios that named
          them still read correctly, and you can restore them below.
        </li>
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={busy} onClick={onConfirm} size="sm" variant="danger">
          Remove {name}
        </Button>
        <Button
          disabled={busy}
          onClick={onCancel}
          size="sm"
          variant="secondary"
        >
          Keep them
        </Button>
      </div>
    </div>
  );
}

/**
 * One tier's bar, the people standing behind it, and whether they clear it.
 *
 * The bar is an INPUT and the three columns beside it are the answer to typing
 * in it — the whole point of putting them on one row is that a manager can move
 * the number and watch the verdict change before publishing anything.
 */
function IncentiveTierRow({
  canEdit,
  onChange,
  row,
  text,
  tierLabel,
  zebra,
}: {
  canEdit: boolean;
  onChange: (value: string) => void;
  row: TierIncentiveRow;
  text: string;
  tierLabel: (tierKey: string) => string;
  zebra: string;
}) {
  const problem = thresholdProblem(text);

  return (
    <tr data-zebra={zebra}>
      <td className="font-medium">{row.label}</td>
      <td className="text-right tabular-nums">
        {canEdit ? (
          <input
            aria-label={`Monthly PNRs to bonus for ${row.label}`}
            aria-invalid={problem !== null}
            className="w-28 rounded border border-border-medium bg-surface-background px-2 py-1 text-right text-sm"
            min={0}
            onChange={(e) => onChange(e.target.value)}
            // "no bar" rather than "0": an empty field is an unset bar, and a
            // zero would be one everybody clears by existing.
            placeholder="no bar"
            step="100"
            type="number"
            value={text}
          />
        ) : row.threshold === null ? (
          <span
            className="text-content-secondary"
            title="No bar has been set for this tier. That is not a bar of zero."
          >
            no bar
          </span>
        ) : (
          pnrs(row.threshold)
        )}
      </td>
      <td>
        {/* EACH PERSON'S OWN NUMBER, beside their name. This is the figure the
            status column compares, and showing only the tier's book made the two
            disagree on screen: a tier can book more than the people staffing it
            do, because some of its customers are pinned to somebody else — so
            the row read "2,805 books, bar 2,500, nobody clears it". */}
        {row.staff.length === 0 ? (
          <span className="text-content-secondary">nobody</span>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {row.staff.map((s) => (
              <li key={s.csmKey}>
                {s.displayName}{' '}
                <span
                  className={
                    row.threshold === null || s.measured === 0
                      ? 'tabular-nums text-content-secondary'
                      : s.monthly >= row.threshold
                        ? 'tabular-nums text-content-success'
                        : 'tabular-nums text-content-danger'
                  }
                  title={
                    `${pnrs(s.pnrs90)} PNRs over the last 90 days across their whole book` +
                    (s.unmeasured > 0
                      ? `, with ${s.unmeasured} customer(s) carrying no PNR data`
                      : '')
                  }
                >
                  {s.measured === 0 ? '—' : pnrs(s.monthly)}
                </span>
                {s.alsoStaffs.length > 0 ? (
                  <span
                    className="ml-1 text-content-secondary text-xs"
                    title={`${s.displayName} also holds seats in ${s.alsoStaffs.map(tierLabel).join(', ')}, but is measured under this bar because this is the tier they are assigned to on this tab. The figure is their WHOLE book — every tier — because that is what a bonus is earned on.`}
                  >
                    (also {s.alsoStaffs.map(tierLabel).join(', ')})
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </td>
      <td className="text-right tabular-nums">
        {/* The TIER's book, not the people's — what these accounts generate,
            whoever ends up holding them. It is the figure that says whether a
            bar survives a change of seats. */}
        {row.bookAccounts === 0 ? (
          <span className="text-content-secondary">—</span>
        ) : (
          <span
            title={`${pnrs(row.bookPnrs90)} PNRs across ${row.bookAccounts} customers in the last 90 days${
              row.bookUnmeasured > 0
                ? `, with ${row.bookUnmeasured} carrying no PNR data`
                : ''
            }`}
          >
            {pnrs(row.bookMonthly)}
          </span>
        )}
      </td>
      <td>
        <IncentiveVerdict row={row} />
      </td>
    </tr>
  );
}

/**
 * What the bar and the books add up to, in words.
 *
 * Every not-a-pass-or-fail case is named rather than folded into a count. "0 of
 * 3 clear" reads as a team missing its targets; when the real reason is that
 * nobody wrote a bar, or nobody staffs the tier, or no PNR data exists, saying
 * so is the difference between a finding and a false accusation.
 */
function IncentiveVerdict({ row }: { row: TierIncentiveRow }) {
  const verdict = tierVerdict(row);
  switch (verdict.kind) {
    case 'no-threshold':
      return (
        <span className="text-content-secondary text-xs">
          No bar set — nothing to earn against.
        </span>
      );
    case 'unstaffed':
      return (
        <span className="text-content-secondary text-xs">
          Nobody staffs this tier, so nobody is measured against the bar.
        </span>
      );
    case 'unmeasured':
      return (
        <span className="text-content-secondary text-xs">
          No PNR data for anyone here — the bar cannot be checked.
        </span>
      );
    case 'all-clear':
      return (
        <span className="text-content-success text-xs">
          All {verdict.people} clear it in an average month.
        </span>
      );
    case 'some-clear':
      return (
        <span className="text-content-warning text-xs">
          {verdict.clears} of {verdict.people} clear it in an average month.
        </span>
      );
    case 'none-clear':
      return (
        <span className="text-content-danger text-xs">
          Nobody clears it — the furthest is {pnrs(verdict.shortest)} PNRs
          short.
        </span>
      );
  }
}

/** One person's customers, biggest first. */
function TeamBook({
  accounts,
  name,
}: {
  accounts: readonly Account[];
  name: string;
}) {
  const ordered = [...accounts].sort(
    (a, b) => b.spendM - a.spendM || a.name.localeCompare(b.name),
  );
  const shown = ordered.slice(0, 25);

  if (ordered.length === 0) {
    return (
      <div className="detail-panel">
        <p className="text-content-secondary text-sm">
          {name} holds no customers in the computed book. That is ordinary for a
          manager who staffs no tier and holds no pins, and a finding for anyone
          else.
        </p>
      </div>
    );
  }

  return (
    <div className="detail-panel flex flex-col gap-1">
      <h4 className="font-medium text-sm">
        {name} — {ordered.length}{' '}
        {ordered.length === 1 ? 'customer' : 'customers'}
      </h4>
      <ul className="flex flex-col gap-0.5">
        {shown.map((a) => (
          <li className="text-content-secondary text-xs" key={a.id}>
            {a.name} <span className="tabular-nums">({volumeM(a.spendM)})</span>
          </li>
        ))}
      </ul>
      {ordered.length > shown.length ? (
        <p className="text-content-secondary text-xs italic">
          and {ordered.length - shown.length} more, smaller than{' '}
          {volumeM(shown[shown.length - 1]!.spendM)}
        </p>
      ) : null}
    </div>
  );
}
